import moment from "moment";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "shared/utils";
import { Hide, Show } from "shared/components";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { ApplicationStatus, IncomeType } from "shared/types";
import { TablesSlice } from "../..";
import { Tabs } from "shared/components/BaseTabs";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { VerifiedReportedStats } from "./VerifiedReportedStats";
import { VerifiedHeader } from "../components/VerifiedHeader";
import { OfferLetterAssets } from "../components/OfferLetterAssets";
import { WarningRecommendation } from "../../components/WarningRecommendation";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import { BankStatementItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/bank-statment";
import {
  CombinedIncome,
  VerificationSpecificAdditionaData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { Dispatch, SetStateAction } from "react";
import { ResumeTooltip } from "shared/app/OnHoldResume/ResumeTooltip";
import { getTabsDisplayMap } from "views/applications-id-report/slices/CombinedIncomeReportSlice/helpers";

export const VerifiedIncomeDetails = ({
  groupedIncomeItems,
  income,
  currentIncomeIndex,
  setCurrentIncomeIndex,
  currentItem,
  applicationId,
  applicationStatus,
  setIsArchivedVerificationSelected,
  additionalData
}: {
  groupedIncomeItems: (VerifiedItem | BankStatementItem[])[];
  income: CombinedIncome;
  currentIncomeIndex: number;
  setCurrentIncomeIndex: Dispatch<SetStateAction<number>>;
  currentItem?: VerifiedItem | VerifiedItem[];
  applicationId?: string | number;
  applicationStatus: ApplicationStatus;
  setIsArchivedVerificationSelected: (value: boolean) => void;
  isManualApplicant: boolean;
  additionalData?: VerificationSpecificAdditionaData | null;
}) => {
  const tabs = getTabsDisplayMap(groupedIncomeItems);
  const currentItemReverifyLog = Array.isArray(currentItem)
    ? currentItem[0]?.reverify_log
    : currentItem?.reverify_log;

  return (
    <>
      {groupedIncomeItems.length > 1 ? (
        <>
          <Tabs
            tabStyle="line"
            size="sm"
            tabs={tabs}
            className="w-fit"
            active={String(currentIncomeIndex)}
            onChange={(t) => {
              const idx = Number(t);
              setCurrentIncomeIndex(idx);

              const item = groupedIncomeItems[idx];
              const isArchived = Array.isArray(item)
                ? item.every((i) => i.is_archived)
                : (item?.is_archived ?? false);
              setIsArchivedVerificationSelected(isArchived);
            }}
          />
          <OfferLetterAssets income={income} />
        </>
      ) : (
        <ContentTitle
          title={
            income.type === IncomeType.OfferLetter
              ? "Offer letter"
              : "Verification method"
          }
        >
          <Show if={income.type === IncomeType.OfferLetter}>
            <WarningRecommendation
              status="Rejected"
              type={income.type}
              rejectionReason={
                "assets" in income && !income.assets?.length
                  ? "No file was uploaded"
                  : ""
              }
              className="mb-4"
            />
          </Show>
          <OfferLetterAssets income={income} />
        </ContentTitle>
      )}

      <Show if={Object.keys(currentItemReverifyLog || {}).length}>
        <div className="flex gap-4 items-center mb-2 mt-4 animate-appear">
          <HugeiconsIcon
            icon={InformationCircleIcon}
            className="text-content-200 shrink-0"
            size={20}
          />

          <p className="text-sm font-medium text-content-400">
            Re-verification was initiated by{" "}
            <span className="font-bold text-content-1000">
              {currentItemReverifyLog?.author?.name}
            </span>{" "}
            on{" "}
            <span className="font-bold text-content-1000">
              {moment(currentItemReverifyLog?.created_at).format(
                "ll [at] h:mm A"
              )}
            </span>
          </p>
        </div>
      </Show>
      <Hide if={income.type === IncomeType.OfferLetter}>
        <div
          key={currentIncomeIndex}
          className={cx(
            "pb-4 animate-appear",
            groupedIncomeItems.length > 1 ? "pt-4" : ""
          )}
        >
          <VerifiedHeader
            currentItem={currentItem}
            income={income}
            reverifyDisabled={applicationStatus === ApplicationStatus.OnHold}
            reverifyTooltip={
              applicationStatus === ApplicationStatus.OnHold ? (
                <ResumeTooltip applicationId={Number(applicationId || 0)} />
              ) : undefined
            }
          />

          <VerifiedReportedStats currentItem={currentItem} />

          {currentItem ? (
            <TablesSlice
              currentItem={currentItem}
              additionalData={additionalData}
            />
          ) : (
            <>
              <p className="text-base text-content-500">
                No verified income sources
              </p>
              <div className="h-[1px] w-full bg-alpha-40 my-3" />
              <div className="flex flex-wrap space-x-4">
                {income.assets?.map(({ asset, global_id }) => (
                  <AssetItem
                    key={global_id}
                    url={asset?.url || ""}
                    content_type={asset?.content_type || ""}
                    filename={asset?.filename || ""}
                    assetName={asset?.filename || ""}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </Hide>
    </>
  );
};
